import { InputRuleApiModel } from '~/lib/api/smartsend/types';
import { IsBooleanRule } from './BooleanRuleHelper';

/**
 * What is wrong with a value under an address rule.
 *
 * Deliberately the same four suffixes `addressHasIssues` pushes onto its issue codes, so the inline field
 * error and the item row error name the same problem. Note the row check is intentionally unanchored
 * where this one is not, so the two can still disagree on a rule whose pattern has no anchors.
 */
export type RuleViolation = 'REQUIRED' | 'FORMAT' | 'MIN-LENGTH' | 'MAX-LENGTH';

/** Relaxed email check: quoting's own pattern is stricter than is worth rejecting a real address on.
 *  Shared with `addressHasIssues` so the field and the row cannot drift apart. */
export const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MatchesPattern = (pattern: string, value: string): boolean => {
  try {
    // Anchored, because the same string is also the input's `pattern` attribute, where the browser
    // anchors it implicitly. Unanchored, a rule carrying no anchors of its own would substring-match.
    return new RegExp(`^(?:${pattern})$`).test(value);
  } catch {
    // Patterns come off the quoting API, so a regex this browser cannot compile is not the customer's
    // fault - let the value through rather than block an address on it.
    return true;
  }
};

/**
 * The first thing wrong with `value` under `rule`, or undefined when it satisfies the rule. Checked in the
 * order `addressHasIssues` reports them; only the first is returned, since a field shows one message.
 */
export const GetRuleViolation = (rule: InputRuleApiModel, value: string | undefined): RuleViolation | undefined => {
  // False is a valid answer for a checkbox, the same exclusion `isValid` in CustomerSelection makes.
  if (IsBooleanRule(rule)) {
    return undefined;
  }

  const entered = value ?? '';

  if (!entered) {
    return rule.required ? 'REQUIRED' : undefined;
  }

  const formatInvalid =
    rule.type === 'email' ? !EmailPattern.test(entered) : !!rule.pattern && !MatchesPattern(rule.pattern, entered);

  if (formatInvalid) {
    return 'FORMAT';
  }

  if (rule.minLength != null && entered.length < rule.minLength) {
    return 'MIN-LENGTH';
  }

  if (rule.maxLength != null && entered.length > rule.maxLength) {
    return 'MAX-LENGTH';
  }

  return undefined;
};
